import * as React from 'react'
import { AuthenticationBody } from '../../components/templates/AuthenticationWrapper'
import colors from '../../globals/colors';
import { createEnDaContent, lang, translateContent } from '../../globals/Translations';
import { TranslatedTerms } from '../../globals/TranslatedTerms';
import { DefaultCheckbox, DynamicPropertyInput } from '../../components/atoms/Inputs';
import { FieldTypes } from '../../shared/KYCPolicy';
import { ColumnFlexBox } from '../../components/templates/Wrappers';
import { TERMS_LINK } from '../../components/organisms/Menu';
import { Auth } from 'aws-amplify';
import { FundbricksFacebookLogin } from './FacebookLogin';
import { IsTablet, useWindowSize } from '../../globals/UIHooks';
import { sendErrorEmail } from '../../globals/ApiHooks';


enum LoginState {
    Login = "LOGIN",
    Signup = "SIGNUP",
    MFA = "MFA",
    ForgotPassword = "FORGOT_PASSWORD",
    SetupNewPassword = "SETUP_NEW_PASSWORD"
}

export const LoginPage: React.FC = () => {
    const [loginState, setLoginState] = React.useState(LoginState.Login)
    const [loading, setLoading] = React.useState<boolean>(false)
    const [error, setError] = React.useState<string>()

    const [email, setEmail] = React.useState<string>()
    const [password, setPassword] = React.useState<string>()
    const [acceptTerms, setAcceptTerms] = React.useState<boolean>(false)
    const [mfa, setMfa] = React.useState<string>()
    const { width } = useWindowSize()

    React.useEffect(() => {
        if (location.pathname === "/signup") setLoginState(LoginState.Signup)
    }, [])

    const isSignup = loginState === LoginState.Signup
    const isLogin = loginState === LoginState.Login
    const isForgotPassword = loginState === LoginState.ForgotPassword
    const isSetupNewPassword = loginState === LoginState.SetupNewPassword

    const isMFA = loginState === LoginState.MFA

    const changeState = (newState: LoginState) => () => {
        setError('')
        setLoginState(newState)
    }

    const mfaMissing = () => setError(translateContent(createEnDaContent("Multi-Factor Authentication is not filled", "2-Faktor godkendelse er ikke udfyldt")))
    const finalError = () => setError(translateContent(createEnDaContent("An error happend. Please contact support", "Der skete en fejl. Venligst kontakt support")))
    const facebookError = () => setError(translateContent(createEnDaContent("An error happend while loggin in with Facebook. Please contact support", "Der skete en fejl ved Facebook login. Venligst kontakt support")))
    const setPasswordMissing = () => setError(translateContent(createEnDaContent("Password is not filled", "Password er ikke udfyldt")))

    const submit = async () => {
        if (!email || email.length < 3) return setError(translateContent(createEnDaContent("Email s not filled", "Email er ikke udfyldt")))
        if (isSignup && !acceptTerms) return setError(lang(TranslatedTerms.ONBOARDING_ERROR_TERMS_MESSAGE))
        setLoading(true)
        const localEmail = (email.toLowerCase()).split(' ').join('')
        try {
            if (loginState === LoginState.Login) {
                if (!password || password.length < 3) return setPasswordMissing()
                await Auth.signIn({ username: localEmail, password })
                location.reload()
            }
            else if (loginState === LoginState.Signup) {
                if (!password || password.length < 3) return setPasswordMissing()
                const { userConfirmed } = await Auth.signUp({
                    username: localEmail,
                    password
                })
                setError('')
                if (!userConfirmed) setLoginState(LoginState.MFA)
            }
            else if (loginState === LoginState.MFA) {
                if (!mfa) {
                    mfaMissing()
                    setLoading(false)
                    return
                }
                if (!password || password.length < 3) return setPasswordMissing()
                await Auth.confirmSignUp(localEmail, mfa)
                await Auth.signIn({ username: localEmail, password })
                location.reload()
            }
            else if (loginState === LoginState.ForgotPassword) {
                await Auth.forgotPassword(localEmail)
                setPassword('')
                setMfa('')
                setError('')
                setLoginState(LoginState.SetupNewPassword)
            }
            else if (loginState === LoginState.SetupNewPassword) {
                if (!password || password.length < 3) return setPasswordMissing()
                if (!mfa) {
                    mfaMissing()
                    setLoading(false)
                    return
                }
                await Auth.forgotPasswordSubmit(localEmail, mfa, password)
                setError('')
                setLoginState(LoginState.Login)
            }
        } catch (error) {
            console.log(error)
            //@ts-ignore
            if (error.name === "UserNotConfirmedException") {
                try {
                    await Auth.resendSignUp(localEmail)
                    setLoginState(LoginState.MFA)
                } catch (error) {
                    await sendErrorEmail({ error: JSON.stringify(error), loginState, localEmail, acceptTerms, passwordLength: password?.length })
                    finalError()
                    console.log(error)
                }
                //@ts-ignore
            } else if (error.name === "UserNotFoundException") {
                setError(translateContent(createEnDaContent("We could not find this account. Maybe you use login with Facebook?", "Vi kunne ikke finde din konto. Måske bruger du log ind med facebook?")))
                //@ts-ignore
            } else if (error.name === "NotAuthorizedException") {
                setError(translateContent(createEnDaContent("Password or email is incorrect", "Password eller email er ikke korrekt")))
                //@ts-ignore
            } else if (error.name === "CodeMismatchException") {
                setError(translateContent(createEnDaContent("MFA Code is not correct", "2-Faktor kode er ikke korrekt")))
                //@ts-ignore
            } else if (error.name === "LimitExceededException") {
                setError(translateContent(createEnDaContent("You have tried to many times, please try again later", "Du har prøvet for mange gange, prøv igen senere")))
                //@ts-ignore
            } else if (error.name === "ExpiredCodeException") {
                setError(translateContent(createEnDaContent("Code is expired", "Koden er uløbet")))
                setLoginState(LoginState.ForgotPassword)
                //@ts-ignore
            } else if (error.name === "UsernameExistsException") {
                setError(translateContent(createEnDaContent("An account with this email already exits. Login instead", "Der findes allerede en konto med denne email. Login i stedet for.")))
                //@ts-ignore
            } else if (error.name === "InvalidPasswordException") {
                setError(translateContent(createEnDaContent("Invalid password. Use at least 8 characters.", "Ugyldigt password. Brug mindst 8 tegn.")))
                //@ts-ignore
            } else if (error.name === "NetworkError") {
                setError(translateContent(createEnDaContent("We experienced a NetworkError. Please try again.", "Vi oplevede en netværks fejl. Venligst prøv igen.")))
                //@ts-ignore
            } else if (error.name === "InvalidParameterException") {
                setError(translateContent(createEnDaContent("One or more parameters are invalid.", "En eller flere parametre er ugyldige.")))
                //@ts-ignore
            } else if (error.name === "PasswordResetRequiredException") {
                setError(translateContent(createEnDaContent("You have to reset you're password", "Du skal nulstille dit password")))
                await Auth.forgotPassword(localEmail)
                setPassword('')
                setMfa('')
                setError('')
                setLoginState(LoginState.SetupNewPassword)
                //@ts-ignore
            } else {
                await sendErrorEmail({ error: JSON.stringify(error), loginState, localEmail, acceptTerms, passwordLength: password?.length })
                console.log(error)
                finalError()
            }

        }

        setLoading(false)
    }

    const current = lang(loginState)

    return (
        <AuthenticationBody loading={loading}>
            <ColumnFlexBox>
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 10, marginBottom: 10 }}>
                    <img
                        width={IsTablet(width) ? "150px" : "250px"}
                        src="https://prod-attachments-bucket-fb-2-0.s3.eu-west-1.amazonaws.com/59c23268-89c9-4132-9a6c-c97a7aa61eff.png"
                        alt="Fundbricks logo" />
                </div>
                <h6 style={{ textAlign: 'center', color: colors.grayText }}>{current}</h6>
                {(isMFA || isSetupNewPassword) && <p>{translateContent(createEnDaContent("We have sent an email with a 6 digit code.", "Vi har sendt en mail med en 6 cifret kode."))}</p>}
                {(isSignup || isForgotPassword || isLogin) && <DynamicPropertyInput fieldName={TranslatedTerms.EMAIL_ADDRESS} type={FieldTypes.EmailField} value={email} setValue={(e) => setEmail(e?.toString())} id="email" />}
                {(isSignup || isLogin) && <DynamicPropertyInput fieldName={TranslatedTerms.PASSWORD} type={FieldTypes.PasswordField} value={password} setValue={(e) => setPassword(e?.toString())} id="password" onEnter={submit} />}
                {isLogin && <a style={{ marginTop: 20 }} onClick={changeState(LoginState.ForgotPassword)}>{translateContent(createEnDaContent("Forgot password", "Glemt adgangskode"))}</a>}

                {(isMFA || isSetupNewPassword) && <DynamicPropertyInput fieldName={"6 cifret kode"} type={FieldTypes.TextField} value={mfa} setValue={(e) => setMfa(e?.toString())} id="code" />}
                {isSetupNewPassword && <DynamicPropertyInput fieldName={translateContent(createEnDaContent("New password", "Ny adgangskode"))} type={FieldTypes.PasswordField} value={password} setValue={(e) => setPassword(e?.toString())} id="new-password" onEnter={submit} />}

                {isSignup && <div style={{ marginTop: 20 }}><DefaultCheckbox handleClick={() => setAcceptTerms(!acceptTerms)}
                    description={<span>{lang(TranslatedTerms.ACCEPT_TERMS_DESCRIPTION)} <a href={TERMS_LINK} target="_blank">{lang(TranslatedTerms.READ_MORE)}</a></span>} /></div>}

                <button onClick={submit} style={{ marginTop: 20, width: '100%' }}>{current}</button>
                {isLogin && <FundbricksFacebookLogin
                    onFail={() => {
                        setLoading(false); facebookError()
                    }}
                    onStart={() => setLoading(true)}
                />}
                {error && error !== '' && <p style={{ color: colors.danger }}>{error}</p>}
                {(isSignup || isLogin) && <span style={{ textAlign: 'center', margin: 20, color: colors.dark }}>{translateContent(createEnDaContent("OR", "ELLER"))}</span>}
                {isLogin && <button className='light' onClick={changeState(LoginState.Signup)}>{lang(TranslatedTerms.SIGNUP)}</button>}
                {(isSignup || isForgotPassword) && <button style={{ marginTop: 20 }} className='light' onClick={changeState(LoginState.Login)}>{lang(TranslatedTerms.LOGIN)}</button>}
                <p className='small'>{translateContent(createEnDaContent("Do you need assistance? Contact: ", "Har du brug for assistance? Kontakt: "))} <a href='mailto:web@fundbricks.com'>web@fundbricks.com</a> </p>
            </ColumnFlexBox>
        </AuthenticationBody>
    )
}
